import Keycloak from 'keycloak-js';

// CONFIGS
import { CORE_DEPENDENCY, CORE_DEPENDENCY_IMPORT_MAP } from './configs';
// GQL QUERY
import POSSIBLE_TYPES_QUERY from './possibleTypes';
// TYPES
import { KeycloakConfig, ImportResponse } from './interfaces';

/**
 * Fetches the import map from the GraphQL API.
 * @param token
 * @param apiUrl
 * @param isLocal
 * @param buildNumber
 */
const getImport = async (
  token: string,
  apiUrl: string,
  isLocal: boolean,
  buildNumber: string
): Promise<ImportResponse> => {
  const response = await fetch(apiUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({
      query: `
        query getMfaImport {
          getMfaImport {
            import
          }
        } 
      `,
    }),
  });

  const data = await response.json();

  if (isLocal) {
    const { data: { getMfaImport } } = data;
    localStorage.setItem(
      `localDbsImportMap-${buildNumber}`,
      getMfaImport.import
    );
  }

  return data;
};

/**
 * Gets the local import map from local storage.
 * @param buildNumber
 */
const getLocalImportMap = (localBuildNumber: string): string | null => {
  return localStorage.getItem(
    `localDbsImportMap-${localBuildNumber}`
  );
};

/**
 * Adds a script tag to the document.
 * @param importMap
 * @param addAfterEl
 * @param src
 */
const addScriptTag = (importMap: any, addAfterEl:HTMLScriptElement, src?:  string) => {
  const scriptTag = document.createElement('script');
  scriptTag.type = 'systemjs-importmap';
  if(src) {
    scriptTag.src = src;
  } else {
    scriptTag.textContent = JSON.stringify(importMap);
  }
  addAfterEl.after(scriptTag);
}

/**
 * Sets the dynamic import map in the document.
 * @param dynamicImportMap
 */
const setDynamicImportMap = (dynamicImportMap:ImportResponse) => {
  const imports = JSON.parse(dynamicImportMap.data.getMfaImport.import);

  const filteredByKey = Object.fromEntries(
    Object.entries(imports).filter(([key]) => !CORE_DEPENDENCY.includes(key)),
  );
  const coreImportMaps = imports['@viax/core-import-map'];

  // add script
  const importMap = {
    imports: {
      ...filteredByKey,
      ...CORE_DEPENDENCY_IMPORT_MAP,
    },
  };

  const defaultImportMapElement = document.getElementById('core-import-map') as HTMLScriptElement;

  addScriptTag(importMap, defaultImportMapElement);

  if(coreImportMaps != defaultImportMapElement.src ) {
    addScriptTag(coreImportMaps, defaultImportMapElement, coreImportMaps);
  }
}

/**
 * Sets the develop import map in the document.
 */
const setDevelopImportMap = () => {
  const devImportMapElement = document.getElementById('develop-import-map') as HTMLScriptElement | null;
  if(devImportMapElement) {
    devImportMapElement.type = 'systemjs-importmap';
  }
}

/**
 * Sets the import map overrides in the document.
 */
const setImportMapOverrides = () => {
  const overrideMap = window.importMapOverrides.getOverrideMap();
  const devImportMapElement = document.getElementById('develop-import-map') as HTMLScriptElement | null;

  if(overrideMap && devImportMapElement) {
    addScriptTag(overrideMap,devImportMapElement);
  }
}

/**
 * Fetches the possible types from the GraphQL schema.
 * and sets it in the window object.
 * @param token
 * @param apiUrl
 */
const getPossibleTypes = async (token: string, apiUrl: string) => {
  try {
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({
        variables: {},
        query: POSSIBLE_TYPES_QUERY,
      }),
    });

    const { data: { __schema: { types } } } = await response.json();

    const possibleTypes: Record<string, string[]> = {};

    types.forEach((supertype: { possibleTypes: any, name: string }) => {
      if (supertype.possibleTypes) {
        possibleTypes[supertype.name] = supertype.possibleTypes.map(
          (subtype: { name: string }) => subtype.name
        );
      }
    });

    window.possibleTypes = possibleTypes;
  } catch (error) {
    console.error('Error fetching possible types:', error);
  }
}

/**
 * Fetches the import map and sets it in the document.
 * @param token
 * @param apiUrl
 * @param callBack
 * @param buildNumber
 * @param isLocal
 */
const getMfaImport = async (
  token: string,
  apiUrl: string,
  callBack: () => void,
  buildNumber: string,
  isLocal: string
) => {
  const useLocal = isLocal === 'true';
  let localConfig: string | null = null;
  if (useLocal) {
    localConfig = getLocalImportMap(buildNumber);
  }
  const importOverwrite: ImportResponse = (useLocal && localConfig)
    ? {
        data: {
          getMfaImport: {
            import: localConfig,
          },
        },
      }
    : await getImport(token, apiUrl, useLocal, buildNumber);

  setDynamicImportMap(importOverwrite);
  setDevelopImportMap();
  setImportMapOverrides();
  callBack();
}

/**
 * Initializes the Keycloak instance and fetches the import map.
 * @param keycloakConfig
 * @param apiUrl
 * @param callBack
 * @param buildNumber
 * @param isLocal
 */
const init = (
  keycloakConfig: KeycloakConfig,
  apiUrl: string,
  callBack: () => void,
  buildNumber: string,
  isLocal: string
) => {

  const keycloak = new Keycloak({
    url: keycloakConfig.url,
    realm: keycloakConfig.realm,
    clientId: keycloakConfig.clientId,
  });

  const initKeycloak = async () => {
    try {
      const authenticated = await keycloak.init({
        onLoad: 'login-required',
        checkLoginIframe: false,
      });

      if (authenticated) {
        console.log('authenticated');
        window.keycloak = keycloak;
        await getPossibleTypes(keycloak.token as string, apiUrl);
        getMfaImport(keycloak.token as string, apiUrl, callBack, buildNumber, isLocal);
      }
    } catch (error) {
      console.log(error);
    }
  }

  initKeycloak();
}

export {
  init,
}
